import type { InputTableQuerySpec, InputWorkflowViewParamInput } from "@selector/shared/graphql";
import { clsx } from "clsx";
import { type ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { ActionButton, Badge, Description, FormError, HStack, Label, Spacer, VStack } from "widgets";
import formStyle from "widgets/form.module.css";
import { useDataAttributes } from "widgets/hooks";

import { CoordinatesListModal } from "@/modules/workflow/components/parameters/CoordinatesListModal.tsx";
import { CoordinatesModal } from "@/modules/workflow/components/parameters/CoordinatesModal.tsx";
import { CoordinatesTile } from "@/modules/workflow/components/parameters/CoordinatesTile";
import {
  useGetTableValueParamForViewIdLazyQuery,
  useSaveTableViewMutation,
} from "@/modules/workflow/graphql/coordinate-lists.gql.ts";
import { useWorkflowEditorStore } from "@/modules/workflow/stores/workflow-editor-store.ts";
import type { TableParamType } from "@/modules/workflow/utils/types.ts";
import { DATATABLE_ID_TYPE, DATATABLE_PARENT_TYPE, ENTITY_TYPE, INVALID_VIEW_PATH, VIEW } from "@/settings/settings.ts";
import { VariantMutantGenomesSelectionActionButton } from "@/ui-modules/genome-viewer/VariantMutantGenomesSelectionForm.tsx";

export interface SelectedCoordinatesPayload {
  coordinateListId: number | null;
  coordinates: number[];
}

interface CoordinatesWidgetProps {
  availableGenomesIds: number[];
  label: string;
  description: string | null;
  optional?: boolean;
  onViewSaved: (viewId: number | null) => void;
  error?: Error;
  extraAction?: ReactElement;
  /** viewId */
  currentValue?: TableParamType;
  isPlaybookExecutionContext?: boolean;
  isDisabled?: boolean;
}
// Component can only be called from WorkflowEditor because of store usage
export const CoordinatesWidget = (props: CoordinatesWidgetProps) => {
  const workflowEventId = useWorkflowEditorStore((state) => state.workflowEvent.id);
  return <CoordinatesWidgetCore {...props} workflowEventId={workflowEventId} />;
};

interface CoordinatesWidgetCoreProps extends CoordinatesWidgetProps {
  workflowEventId: number | undefined;
}

// Generic component, can also be called from Playbook config and execution
export const CoordinatesWidgetCore = ({
  availableGenomesIds,
  label,
  description,
  optional,
  onViewSaved,
  error,
  workflowEventId,
  currentValue,
  isPlaybookExecutionContext,
  isDisabled,
  extraAction,
  ...props
}: CoordinatesWidgetCoreProps) => {
  // data-*
  const dataAttributes = useDataAttributes(props);

  // Dialogs - UI
  const [showCoordinateListGrid, setShowCoordinateGridList] = useState(false);
  const [showCoordinatesGrid, setShowCoordinatesGrid] = useState(false);

  // Query - Load the view
  const [getTableValueParamForViewId] = useGetTableValueParamForViewIdLazyQuery();

  // Data
  const noGenomesAvailable = availableGenomesIds.length === 0;
  const [selectedGenomeId, setSelectedGenomeId] = useState<number | null>(null);
  const [selectedCoordinatesList, setSelectedCoordinatesList] = useState<number | null>(null);
  const [selectedCoordinates, setSelectedCoordinates] = useState<number[] | null>(null);
  const [selectedVariantGenomes, setSelectedVariantGenomes] = useState<number[] | null>(null);
  const [viewRowFilter, setViewRowFilter] = useState<string | undefined>(undefined);
  const [isLoadingView, setIsLoadingView] = useState(false);
  const [variantSelectionError, setVariantSelectionError] = useState<string | null>(null);

  // Track view id changes so we can reset when the view is cleared.
  const normalizedViewId = currentValue ?? null;
  const previousViewId = useRef<number | null>(normalizedViewId);
  const [currentViewId, setCurrentViewId] = useState<number | null>(normalizedViewId);
  const availableGenomesIdsRef = useRef<number[]>(availableGenomesIds);
  const [saveCoordinatesView] = useSaveTableViewMutation();

  const reset = useCallback(() => {
    onViewSaved(null);
    setSelectedGenomeId(null);
    setSelectedCoordinatesList(null);
    setSelectedCoordinates(null);
    setSelectedVariantGenomes(null);
    setViewRowFilter(undefined);
  }, [onViewSaved]);

  useEffect(() => {
    if (normalizedViewId !== previousViewId.current) {
      previousViewId.current = normalizedViewId;
      setCurrentViewId(normalizedViewId);
    }
  }, [normalizedViewId]);

  const handleSaveSelection = useCallback(
    async (coordinates: number[], variantGenomeIds: number[], rowFilter?: string) => {
      if (isDisabled) {
        return;
      }

      if (!selectedCoordinatesList || !selectedGenomeId) {
        throw new Error("Please select a coordinate list first");
      }

      const filteredVariantIds = variantGenomeIds.filter((id): id is number => id != null);

      const tableQuerySpec: InputTableQuerySpec = {
        id: selectedCoordinatesList,
        idType: DATATABLE_ID_TYPE.ENTITY,
        queryType: DATATABLE_PARENT_TYPE.COORD_LIST,
        resultType: ENTITY_TYPE.COORDINATE,
        restrictions: {
          refGenomeId: selectedGenomeId,
          variantGenomeIds: filteredVariantIds,
        },
        rowFilter,
      };

      const viewInput: InputWorkflowViewParamInput = {
        viewLabel: VIEW.TABLE_VIEW.title,
        viewType: VIEW.TABLE_VIEW.type,
        path: INVALID_VIEW_PATH,
        workflowEventId,
        state: {
          tableView: {
            tableQuerySpec,
            selectedIds: coordinates,
          },
        },
      };

      const { data } = await saveCoordinatesView({ variables: { viewInput } });
      const viewId = data?.workflow.saveTableParamViewInput.value?.id ?? null;

      setSelectedCoordinates(coordinates);
      setSelectedVariantGenomes(filteredVariantIds);
      setViewRowFilter(rowFilter);
      onViewSaved(viewId);
    },
    [isDisabled, onViewSaved, saveCoordinatesView, selectedCoordinatesList, selectedGenomeId, workflowEventId],
  );

  // Reset widget if there is no genome selected up in the activity tree
  useEffect(() => {
    if (!availableGenomesIds.length) {
      reset();
    }
  }, [availableGenomesIds.length, reset]);

  useEffect(() => {
    if (!selectedGenomeId) {
      return;
    }

    if (!availableGenomesIds.includes(selectedGenomeId)) {
      // genomes in the upper tree probably changed, so we force reset
      reset();
    }
  }, [availableGenomesIds, reset, selectedGenomeId]);

  useEffect(() => {
    availableGenomesIdsRef.current = availableGenomesIds;
  }, [availableGenomesIds]);

  useEffect(() => {
    if (currentViewId) {
      setIsLoadingView(true);
      getTableValueParamForViewId({ variables: { viewId: currentViewId } })
        .then(({ data }) => {
          const selectedList = data?.workflow.getTableValueParamForViewId.entityId ?? null;
          const tableValue = data?.workflow.getTableValueParamForViewId.value;
          const tableQuerySpec = tableValue?.state?.tableView?.tableQuerySpec;
          const selectedCoordinatesIds = tableValue?.state?.tableView?.selectedIds?.filter((id) => id != null) ?? null;
          const genomeId = tableQuerySpec?.restrictions?.refGenomeId ?? null;
          const variantGenomes = tableQuerySpec?.restrictions?.variantGenomeIds?.filter((id) => id != null) ?? null;

          if (genomeId && availableGenomesIdsRef.current.includes(genomeId)) {
            setSelectedCoordinates(selectedCoordinatesIds);
            setSelectedGenomeId(genomeId);
            setSelectedCoordinatesList(selectedList);
            setSelectedVariantGenomes(variantGenomes);
            setViewRowFilter(tableQuerySpec?.rowFilter ?? undefined);
          }
        })
        .finally(() => {
          setIsLoadingView(false);
        });
    } else {
      setIsLoadingView(false);
      setSelectedGenomeId(null);
      setSelectedCoordinatesList(null);
      setSelectedCoordinates(null);
      setSelectedVariantGenomes(null);
      setViewRowFilter(undefined);
    }
  }, [currentViewId, getTableValueParamForViewId]);

  useEffect(() => {
    setVariantSelectionError(null);
  }, [selectedCoordinatesList, selectedGenomeId, isPlaybookExecutionContext]);

  useEffect(() => {
    if (isDisabled) {
      setShowCoordinateGridList(false);
      setShowCoordinatesGrid(false);
    }
  }, [isDisabled]);

  const handleVariantGenomesSelection = useCallback(
    (variantGenomeIds: number[]) => {
      if (isDisabled) {
        return;
      }

      if (!selectedCoordinatesList || !selectedGenomeId) {
        setVariantSelectionError("Please select a coordinate list first");
        return;
      }

      setVariantSelectionError(null);
      handleSaveSelection(selectedCoordinates ?? [], variantGenomeIds, viewRowFilter).catch((error) => {
        const message = error instanceof Error ? error.message : "Unable to save variant genomes";
        setVariantSelectionError(message);
      });
    },
    [handleSaveSelection, isDisabled, selectedCoordinates, selectedCoordinatesList, selectedGenomeId, viewRowFilter],
  );

  const isSelectCoordinatesDisabled = noGenomesAvailable || !selectedCoordinatesList || isLoadingView || isDisabled;
  const isVariantSelectionDisabled = isSelectCoordinatesDisabled || !selectedGenomeId;

  return (
    <>
      <VStack
        className={formStyle.input_layout}
        data-testid={`${dataAttributes["data-testid"] ? `${dataAttributes["data-testid"]}__` : ""}multi-select__button`}
      >
        {!!label && (
          <HStack className={formStyle.input_layout_column1} style={{ alignItems: "baseline" }}>
            <Label>{label}</Label>
            <Spacer />
            {optional && <Badge intent="light" large inline label="optional" style={{ flex: "0 0 auto" }} />}
          </HStack>
        )}
        <Description
          data-testid="sample-selection-description"
          className={clsx(formStyle.input_layout_column1, {
            [formStyle.description_with_tag]: label != undefined && optional,
          })}
        >
          {description}
        </Description>
        <div className={formStyle.input_layout_column1}>
          <CoordinatesTile
            selectedCoordinateList={selectedCoordinatesList}
            selectedCoordinatesCount={(selectedCoordinates ?? []).length}
            selectedVariants={selectedVariantGenomes ?? []}
            isLoadingView={isLoadingView}
          />
        </div>
        {(error || noGenomesAvailable) && (
          <FormError data-testid={"coordinate-list-field-error"}>
            {error ? error.message : noGenomesAvailable ? "Please select a genome first" : null}
          </FormError>
        )}
        <HStack className={formStyle.input_layout_column1} style={{ width: "100%", paddingTop: "3px" }} wrap>
          <ActionButton
            label={"Select List"}
            isDisabled={noGenomesAvailable || isLoadingView || isDisabled}
            onPress={() => setShowCoordinateGridList(true)}
            style={{ flex: 1 }}
          />
          {isPlaybookExecutionContext ? (
            isVariantSelectionDisabled ? (
              <ActionButton label={"Select Variant Genomes"} isDisabled style={{ flex: 1 }} />
            ) : (
              <VariantMutantGenomesSelectionActionButton
                onSave={handleVariantGenomesSelection}
                genomeId={selectedGenomeId!}
                restrictionVariantGenomesIds={selectedVariantGenomes ?? undefined}
                style={{ flex: 1 }}
              />
            )
          ) : (
            <ActionButton
              label={"Select Coordinates"}
              isDisabled={isSelectCoordinatesDisabled}
              onPress={() => setShowCoordinatesGrid(true)}
              style={{ flex: 1 }}
            />
          )}
        </HStack>
        {isPlaybookExecutionContext && variantSelectionError && (
          <FormError data-testid="variant-selection-error">{variantSelectionError}</FormError>
        )}
        {!!extraAction && <HStack className={formStyle.input_layout_column2}>{extraAction}</HStack>}
      </VStack>
      <CoordinatesListModal
        isOpen={!isDisabled && showCoordinateListGrid}
        defaultValue={{
          genomeId: selectedGenomeId,
          listId: selectedCoordinatesList,
        }}
        availableGenomesIds={availableGenomesIds}
        onClose={(next) => {
          setShowCoordinateGridList(false);

          if (next) {
            setShowCoordinatesGrid(true);
          }
        }}
        onSave={(viewId, genomeId, listId) => {
          onViewSaved(viewId);
          setSelectedCoordinatesList(listId);
          setSelectedGenomeId(genomeId);
          setSelectedCoordinates(null);
          setSelectedVariantGenomes(null);
          setViewRowFilter(undefined);
        }}
        workflowEventId={workflowEventId}
      />
      {!isPlaybookExecutionContext && (
        <CoordinatesModal
          isOpen={!isDisabled && showCoordinatesGrid}
          onClose={() => setShowCoordinatesGrid(false)}
          defaultValue={{
            genomeId: selectedGenomeId,
            coordinateListId: selectedCoordinatesList,
            selectedIds: selectedCoordinates ?? [],
            restrictionsIds: selectedVariantGenomes ?? [],
            rowFilter: viewRowFilter,
          }}
          onSaveSelection={(coordinates, variantGenomes, rowFilter) =>
            handleSaveSelection(coordinates, variantGenomes, rowFilter)
          }
        />
      )}
    </>
  );
};
